import { useSetRecoilState } from 'recoil';
import { ApiUrl } from '@/utils/constants';
import useSetLoading from '@/hooks/useSetLoading';
import useInsertServiceIdInApiUrl from '@/hooks/useInsertServiceIdInApiUrl';
import { AxiosResponse } from 'axios';
import { BillingDetailResponse } from '@/types/api/mypage/billing';
import { billingDetailState, notFoundState } from '@/states/billingDetailState';
import useCommonApiErrorHandling from '@/hooks/useCommonApiErrorHandling';
import { useCallback } from 'react';
import api from '@/utils/api';
import { getSigV4HeadersForGet, isExpiredToken } from '@/utils/cognito';
import url from '@/utils/config';

/**
 * 利用料金明細取得フック。
 * 指定年月の利用明細を取得し、明細画面で参照するstateへ保存する。
 *
 * @returns execute - API実行関数
 */
/**
 * 利用料金明細取得hook
 * @returns execute API実行関数
 */
const useGetBilling = () => {
  const setBillingDetail = useSetRecoilState(billingDetailState);
  const setNotFound = useSetRecoilState(notFoundState);

  // hooks
  const { startLoading, finishLoading } = useSetLoading();
  const { insertServiceIdInApiUrl } = useInsertServiceIdInApiUrl();
  const { commonErrorHandling, commonResponseCodeHandling } = useCommonApiErrorHandling();

  /**
   * API実行関数
   */
  const execute = useCallback(async ({ year, month }: { year: string; month: string }) => {
    // 月別明細APIは年月がURLパラメータのため、対象年月をパスへ埋め込む。
    const apiUrl = ApiUrl.Read.BILLING_INFO_BY_MONTH_V2.replace(
      ':target_month',
      `${year}-${month}`
    );
    const apiUrlWithServiceId = insertServiceIdInApiUrl(apiUrl);
    const headers = await getSigV4HeadersForGet(apiUrlWithServiceId);

    startLoading(apiUrlWithServiceId);

    await api
      .get(apiUrlWithServiceId, headers)
      .then((res: AxiosResponse<BillingDetailResponse>) => {
        const { isError } = commonResponseCodeHandling({
          responseCode: res.data.response_code,
          isTransitionTop: true,
        });

        if (isError) return;

        // 表示コンポーネントから共通参照できるよう、取得結果をRecoilへ保存する。
        setBillingDetail(res.data);
      })
      .catch((error: any) => {
        // ログイントークンの有効期限が切れていたら、ログイン画面に遷移
        if (isExpiredToken(error)) {
          sessionStorage.clear();
          window.location.href = url('/login');
          return;
        }

        if (error.response.status === 404) {
          // 対象月の明細が存在しないケースは異常ではないため、未取得状態として扱う。
          setNotFound(true);
        } else {
          commonErrorHandling({ error, isTransitionTop: true });
        }
      })
      .finally(() => finishLoading(apiUrlWithServiceId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { execute };
};

export default useGetBilling;
